import React, { forwardRef, memo } from 'react'
import Image from 'next/image'

import { shouldUseNextImage } from '@/services/functions'

interface ImageNextProps
  extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src'> {
  src?: string
  width?: number
  height?: number
  alt?: string
  autoSize?: boolean
  priority?: boolean
  quality?: number
  fetchPriority?: 'high' | 'low' | 'auto'
}

const ImageNext = (
  {
    src,
    width,
    height,
    alt = '',
    autoSize,
    priority = false,
    quality = 75,
    loading,
    fetchPriority,
    ...rest
  }: ImageNextProps,
  ref: React.Ref<HTMLImageElement>
) => {
  if (!src) return null

  const useNext: boolean = shouldUseNextImage()

  if (useNext) {
    if (autoSize) {
      return (
        <Image
          ref={ref}
          src={src}
          alt={alt}
          fill
          sizes="(max-width: 768px) 100vw, (max-width: 1200px) 70vw, 50vw"
          priority={priority}
          fetchPriority={priority ? fetchPriority : undefined}
          quality={quality}
          style={{ objectFit: 'contain' }}
          {...rest}
        />
      )
    }

    return (
      <Image
        ref={ref}
        src={src}
        width={width || 800}
        height={height || 600}
        alt={alt}
        priority={priority}
        fetchPriority={fetchPriority}
        quality={quality}
        loading={priority ? 'eager' : 'lazy'}
        {...rest}
      />
    )
  }

  return (
    <img
      ref={ref}
      src={src}
      width={width}
      height={height}
      alt={alt}
      loading={priority ? 'eager' : 'lazy'}
      decoding="async"
      {...(fetchPriority ? { fetchpriority: fetchPriority } : {})}
      {...rest}
    />
  )
}

export default memo(forwardRef(ImageNext))
